// admin/src/pages/ScimIntegrations/ConnectionDetailDrawer.js

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Drawer,
  Box,
  Typography,
  Stack,
  IconButton,
  Divider,
  Chip,
  Button,
  TextField,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Alert,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import KeyIcon from '@mui/icons-material/Key';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import {
  getConnectionById,
  updateConnection,
  enableConnection,
  disableConnection,
  deleteConnection,
  issueToken,
  rotateToken,
  revokeToken,
  extractScimErrorMessage,
} from './scimApi';
import { getStatusChipSx } from './scimStatusStyle';

export default function ConnectionDetailDrawer({
  open,
  connectionId,
  onClose,
  canCreate,
  canUpdate,
  canDelete,
  onTokenIssued,
  onConnectionDeleted,
  onConnectionUpdated,
}) {
  const [connection, setConnection] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copiedUrl, setCopiedUrl] = useState(false);

  // Edit connection dialog state
  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [editProvider, setEditProvider] = useState('generic');
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState(null);

  // Delete typed confirmation dialog state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [typedName, setTypedName] = useState('');
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  // Rotate token dialog state
  const [rotateOpen, setRotateOpen] = useState(false);
  const [revokeOldest, setRevokeOldest] = useState(true);
  const [rotateBusy, setRotateBusy] = useState(false);
  const [rotateError, setRotateError] = useState(null);

  // Revoke token dialog state
  const [revokeTargetToken, setRevokeTargetToken] = useState(null);
  const [revokeReason, setRevokeReason] = useState('');
  const [revokeBusy, setRevokeBusy] = useState(false);
  const [revokeError, setRevokeError] = useState(null);

  // Issue token state
  const [issueBusy, setIssueBusy] = useState(false);
  const [issueError, setIssueError] = useState(null);

  const loadDetails = async () => {
    if (!connectionId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getConnectionById(connectionId);
      if (res?.data) {
        setConnection(res.data);
      }
    } catch (err) {
      setError(extractScimErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && connectionId) {
      loadDetails();
    } else {
      setConnection(null);
      setError(null);
    }
  }, [open, connectionId]);

  const handleCopyBaseUrl = async () => {
    if (connection?.scimBaseUrl && navigator?.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(connection.scimBaseUrl);
        setCopiedUrl(true);
        setTimeout(() => setCopiedUrl(false), 2500);
      } catch (e) {
        setCopiedUrl(true);
        setTimeout(() => setCopiedUrl(false), 2500);
      }
    } else {
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2500);
    }
  };

  const handleToggleStatus = async () => {
    if (!connection || !canUpdate) return;
    try {
      if (connection.status === 'active') {
        await disableConnection(connection._id);
      } else {
        await enableConnection(connection._id);
      }
      await loadDetails();
      if (onConnectionUpdated) onConnectionUpdated();
    } catch (err) {
      setError(extractScimErrorMessage(err));
    }
  };

  const handleOpenEdit = () => {
    if (!connection) return;
    setEditName(connection.name || '');
    setEditProvider(connection.providerType || 'generic');
    setEditError(null);
    setEditOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editName.trim()) {
      setEditError('Connection name cannot be empty.');
      return;
    }
    setEditSaving(true);
    setEditError(null);
    try {
      await updateConnection(connection._id, {
        name: editName.trim(),
        providerType: editProvider,
      });
      setEditOpen(false);
      await loadDetails();
      if (onConnectionUpdated) onConnectionUpdated();
    } catch (err) {
      setEditError(extractScimErrorMessage(err));
    } finally {
      setEditSaving(false);
    }
  };

  const handleDeleteConnection = async () => {
    if (typedName !== connection?.name) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await deleteConnection(connection._id);
      setDeleteOpen(false);
      onClose();
      if (onConnectionDeleted) onConnectionDeleted(connection._id);
    } catch (err) {
      setDeleteError(extractScimErrorMessage(err));
      setDeleteBusy(false);
    }
  };

  const handleIssueToken = async () => {
    if (!canCreate || !connection) return;
    setIssueBusy(true);
    setIssueError(null);
    try {
      const res = await issueToken(connection._id);
      await loadDetails();
      if (res?.data?.plaintextToken) {
        onTokenIssued(res.data);
      }
    } catch (err) {
      setIssueError(extractScimErrorMessage(err));
    } finally {
      setIssueBusy(false);
    }
  };

  const handleRotateToken = async () => {
    if (!canUpdate || !connection) return;
    setRotateBusy(true);
    setRotateError(null);
    try {
      const res = await rotateToken(connection._id, { revokeOldest });
      setRotateOpen(false);
      await loadDetails();
      if (res?.data?.plaintextToken) {
        onTokenIssued(res.data);
      }
    } catch (err) {
      setRotateError(extractScimErrorMessage(err));
    } finally {
      setRotateBusy(false);
    }
  };

  const handleRevokeToken = async () => {
    if (!canDelete || !connection || !revokeTargetToken) return;
    if (!revokeReason.trim()) {
      setRevokeError('Revocation reason is required.');
      return;
    }
    setRevokeBusy(true);
    setRevokeError(null);
    try {
      await revokeToken(connection._id, {
        tokenId: revokeTargetToken.tokenId,
        reason: revokeReason.trim(),
      });
      setRevokeTargetToken(null);
      setRevokeReason('');
      await loadDetails();
    } catch (err) {
      setRevokeError(extractScimErrorMessage(err));
    } finally {
      setRevokeBusy(false);
    }
  };

  const credentialsList = connection?.credentials || [];
  const activeCredentials = credentialsList.filter((c) => c.status === 'active');
  const atMaxTokens = activeCredentials.length >= 2;

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: { width: { xs: '100%', sm: 600, md: 680 }, p: 3 },
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            SCIM Connection Details
          </Typography>
          <IconButton onClick={onClose} aria-label="Close detail drawer" size="small">
            <CloseIcon />
          </IconButton>
        </Stack>

        <Divider sx={{ mb: 3 }} />

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={8}>
            <CircularProgress size={36} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : !connection ? (
          <Typography color="text.secondary">No connection details available.</Typography>
        ) : (
          <Stack spacing={3}>
            {/* Header / Info Card */}
            <Paper variant="outlined" sx={{ p: 2.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                <div>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {connection.name}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      size="small"
                      label={connection.providerType?.toUpperCase()}
                      variant="outlined"
                      sx={{ fontWeight: 600, textTransform: 'uppercase' }}
                    />
                    <Chip
                      size="small"
                      label={connection.status}
                      sx={getStatusChipSx(connection.status)}
                    />
                  </Stack>
                </div>

                <Stack direction="row" spacing={1}>
                  {canUpdate && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={handleOpenEdit}
                      id="edit-connection-btn"
                    >
                      Edit
                    </Button>
                  )}
                  {canUpdate && (
                    <Button
                      size="small"
                      variant="outlined"
                      color={connection.status === 'active' ? 'warning' : 'success'}
                      startIcon={connection.status === 'active' ? <BlockIcon /> : <CheckCircleOutlineIcon />}
                      onClick={handleToggleStatus}
                      id="toggle-status-btn"
                    >
                      {connection.status === 'active' ? 'Disable' : 'Enable'}
                    </Button>
                  )}
                  {canDelete && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteForeverIcon />}
                      onClick={() => {
                        setTypedName('');
                        setDeleteError(null);
                        setDeleteOpen(true);
                      }}
                      id="delete-connection-btn"
                    >
                      Delete
                    </Button>
                  )}
                </Stack>
              </Stack>

              {/* SCIM Base URL */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  SCIM 2.0 Base URL (Endpoint for IdP)
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 1.25,
                    mt: 0.5,
                    bgcolor: 'action.hover',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography
                    component="code"
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: '0.85rem',
                      wordBreak: 'break-all',
                      mr: 1,
                    }}
                  >
                    {connection.scimBaseUrl || '—'}
                  </Typography>
                  <Tooltip title={copiedUrl ? 'Copied!' : 'Copy SCIM URL'}>
                    <IconButton
                      onClick={handleCopyBaseUrl}
                      aria-label="Copy SCIM Base URL"
                      size="small"
                      color={copiedUrl ? 'success' : 'default'}
                    >
                      {copiedUrl ? <CheckCircleIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              <Stack direction="row" spacing={3} sx={{ mt: 2.5 }}>
                <div>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Created At
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {connection.createdAt ? new Date(connection.createdAt).toLocaleString() : '—'}
                  </Typography>
                </div>
                <div>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Last Synchronized
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {connection.lastSyncAt ? new Date(connection.lastSyncAt).toLocaleString() : 'Never'}
                  </Typography>
                </div>
              </Stack>
            </Paper>

            {/* Token Lifecycle Management */}
            <div>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                <div>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Bearer Tokens
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Active tokens: {activeCredentials.length} / 2 allowed
                  </Typography>
                </div>

                <Stack direction="row" spacing={1}>
                  {canCreate && (
                    <Tooltip
                      title={
                        atMaxTokens
                          ? 'Maximum of 2 active tokens reached. Rotate or revoke a token first.'
                          : 'Issue a secondary active bearer token'
                      }
                    >
                      <span>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<KeyIcon />}
                          disabled={atMaxTokens || issueBusy}
                          onClick={handleIssueToken}
                          id="issue-token-btn"
                        >
                          {issueBusy ? <CircularProgress size={16} /> : 'Issue Token'}
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                  {canUpdate && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<RefreshIcon />}
                      onClick={() => {
                        setRevokeOldest(true);
                        setRotateError(null);
                        setRotateOpen(true);
                      }}
                      id="rotate-token-btn"
                    >
                      Rotate
                    </Button>
                  )}
                </Stack>
              </Stack>

              {issueError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setIssueError(null)}>
                  {issueError}
                </Alert>
              )}

              <TableContainer component={Paper} variant="outlined">
                <Table size="small" aria-label="Credentials table">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Token Prefix</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Last Used</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        Action
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {credentialsList.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                          No tokens generated yet.
                        </TableCell>
                      </TableRow>
                    ) : (
                      credentialsList.map((cred) => (
                        <TableRow key={cred.tokenId}>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>
                            {cred.tokenPrefix || `${cred.tokenId.slice(0, 8)}...`}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={cred.status}
                              sx={{ ...getStatusChipSx(cred.status), fontSize: '0.75rem' }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.8rem' }}>
                            {cred.createdAt ? new Date(cred.createdAt).toLocaleDateString() : '—'}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.8rem' }}>
                            {cred.lastUsedAt ? new Date(cred.lastUsedAt).toLocaleString() : 'Never'}
                          </TableCell>
                          <TableCell align="right">
                            {cred.status === 'active' && canDelete ? (
                              <Button
                                size="small"
                                color="error"
                                onClick={() => {
                                  setRevokeTargetToken(cred);
                                  setRevokeReason('');
                                  setRevokeError(null);
                                }}
                                id={`revoke-token-${cred.tokenId}`}
                              >
                                Revoke
                              </Button>
                            ) : cred.status === 'revoked' ? (
                              <Typography variant="caption" color="text.secondary">
                                Revoked
                              </Typography>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          </Stack>
        )}
      </Drawer>

      {/* Edit Connection Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Edit Connection</DialogTitle>
        <DialogContent dividers>
          {editError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {editError}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Connection Name"
            fullWidth
            required
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            id="edit-connection-name-input"
          />
          <FormControl fullWidth margin="dense" sx={{ mt: 2 }}>
            <InputLabel id="edit-provider-label">Provider Type</InputLabel>
            <Select
              labelId="edit-provider-label"
              value={editProvider}
              label="Provider Type"
              onChange={(e) => setEditProvider(e.target.value)}
              id="edit-provider-select"
            >
              <MenuItem value="generic">Generic SCIM 2.0</MenuItem>
              <MenuItem value="entra">Microsoft Entra ID</MenuItem>
              <MenuItem value="okta">Okta</MenuItem>
              <MenuItem value="ping">PingFederate</MenuItem>
              <MenuItem value="onelogin">OneLogin</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setEditOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveEdit}
            disabled={editSaving}
            id="save-edit-connection-btn"
          >
            {editSaving ? <CircularProgress size={18} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Connection Typed Confirmation Dialog */}
      <Dialog
        open={deleteOpen}
        onClose={() => !deleteBusy && setDeleteOpen(false)}
        maxWidth="xs"
        fullWidth
        aria-labelledby="delete-connection-title"
      >
        <DialogTitle id="delete-connection-title" sx={{ color: 'error.main', fontWeight: 700 }}>
          Delete Connection
        </DialogTitle>
        <DialogContent dividers>
          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deleteError}
            </Alert>
          )}
          <DialogContentText sx={{ mb: 2, color: 'text.primary' }}>
            This action is <strong>irreversible</strong>. Deleting this connection will permanently revoke all bearer
            credentials and stop provisioning requests from the identity provider.
          </DialogContentText>
          <DialogContentText sx={{ mb: 2 }}>
            To confirm deletion, type the connection name <strong>{connection?.name}</strong> below:
          </DialogContentText>
          <TextField
            fullWidth
            size="small"
            placeholder={connection?.name}
            value={typedName}
            onChange={(e) => setTypedName(e.target.value)}
            id="delete-typed-confirmation-input"
            data-testid="delete-typed-confirmation-input"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteOpen(false)} color="inherit" disabled={deleteBusy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={typedName !== connection?.name || deleteBusy}
            onClick={handleDeleteConnection}
            id="confirm-delete-connection-btn"
            data-testid="confirm-delete-connection-btn"
          >
            {deleteBusy ? <CircularProgress size={18} color="inherit" /> : 'Delete Connection'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rotate Token Dialog */}
      <Dialog open={rotateOpen} onClose={() => !rotateBusy && setRotateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Rotate SCIM Bearer Token</DialogTitle>
        <DialogContent dividers>
          {rotateError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {rotateError}
            </Alert>
          )}
          <DialogContentText sx={{ mb: 2 }}>
            Rotating will issue a new high-entropy bearer token. Make sure to configure the new token in your IdP immediately.
          </DialogContentText>
          <FormControlLabel
            control={
              <Checkbox
                checked={revokeOldest}
                onChange={(e) => setRevokeOldest(e.target.checked)}
                color="primary"
                id="rotate-revoke-oldest-checkbox"
              />
            }
            label="Revoke the oldest active credential upon issuance"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setRotateOpen(false)} color="inherit" disabled={rotateBusy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleRotateToken}
            disabled={rotateBusy}
            id="confirm-rotate-token-btn"
          >
            {rotateBusy ? <CircularProgress size={18} /> : 'Generate New Token'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revoke Token Dialog */}
      <Dialog open={Boolean(revokeTargetToken)} onClose={() => !revokeBusy && setRevokeTargetToken(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: 'error.main', fontWeight: 700 }}>Revoke Bearer Token</DialogTitle>
        <DialogContent dividers>
          {revokeError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {revokeError}
            </Alert>
          )}
          <DialogContentText sx={{ mb: 2 }}>
            Revoking token <strong>{revokeTargetToken?.tokenPrefix || revokeTargetToken?.tokenId}</strong> will
            immediately block all inbound SCIM requests authenticated with it.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            required
            label="Revocation Reason"
            placeholder="e.g. Key compromised, scheduled rotation"
            value={revokeReason}
            onChange={(e) => setRevokeReason(e.target.value)}
            id="revoke-reason-input"
            data-testid="revoke-reason-input"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setRevokeTargetToken(null)} color="inherit" disabled={revokeBusy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={!revokeReason.trim() || revokeBusy}
            onClick={handleRevokeToken}
            id="confirm-revoke-token-btn"
            data-testid="confirm-revoke-token-btn"
          >
            {revokeBusy ? <CircularProgress size={18} color="inherit" /> : 'Revoke Token'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

ConnectionDetailDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  connectionId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  canCreate: PropTypes.bool.isRequired,
  canUpdate: PropTypes.bool.isRequired,
  canDelete: PropTypes.bool.isRequired,
  onTokenIssued: PropTypes.func.isRequired,
  onConnectionDeleted: PropTypes.func,
  onConnectionUpdated: PropTypes.func,
};
